import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { ConfirmPaymentData, StripeElementLocale, StripeElements, StripeElementsOptionsMode, loadStripe } from "@stripe/stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { FormEvent, PropsWithChildren, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAlert } from "capitalrx-components-2.0/lib/general";

import { confirmPaymentIntent, initiatePaymentIntent } from "@/App/api/medicarePrescriptionPaymentPlan";
import { AuthenticationContext, MemberEligibilityContext } from "@/App/context";
import { PaymentMethod } from "@/App/types/medicarePrescriptionPaymentPlan";
import { usePlanYear } from "../PlanYearContext";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.stripePaymentForm";

type StripePaymentConfirmationOptions = {
  elements?: StripeElements;
  clientSecret: string;
  confirmParams?: Partial<ConfirmPaymentData>;
  redirect: "if_required";
};
type StripePaymentOptions = {
  amount: Decimal;
  paymentMethod: PaymentMethod;
};
type StripeFormProps = StripePaymentOptions & {
  submitRef: React.LegacyRef<HTMLButtonElement>;
  showPaymentMethodForm: boolean;
  handlePaymentSuccess?: (customerId?: string) => Promise<void>;
  setLoading: (isLoading: boolean) => void;
  handleLoadingError: () => void;
};

export const StripePaymentForm = ({
  amount,
  setLoading,
  paymentMethod,
  submitRef,
  handlePaymentSuccess,
  handleLoadingError,
  showPaymentMethodForm,
}: StripeFormProps) => {
  const { t: translate } = useTranslation();

  const stripe = useStripe();
  const elements = useElements();
  const showAlert = useAlert();
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryClient = useQueryClient();
  const planYear = usePlanYear();

  const url = window.location.href;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleErrorMessage = (err: any) => {
    showAlert({
      message: typeof err === "string" ? err : err?.data?.message || err.message,
      type: "error",
      style: "banner",
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    setLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleErrorMessage(submitError);
      setLoading(false);
      return;
    }

    try {
      const { client_secret, customer_id, session_id } = await initiatePaymentIntent(await getAuthToken(), {
        preferred_payment_method: paymentMethod,
        amount: amount.times(100).toNumber(),
        using_saved_preferred_payment_method: !showPaymentMethodForm,
        plan_year: planYear,
      });

      const paymentConfirmationData: StripePaymentConfirmationOptions = {
        clientSecret: client_secret,
        redirect: "if_required",
      };

      if (showPaymentMethodForm) {
        paymentConfirmationData.elements = elements;
        paymentConfirmationData.confirmParams = { return_url: url };
      }

      const paymentConfirmResponse = await stripe.confirmPayment(paymentConfirmationData);
      if (paymentConfirmResponse.error) {
        handleErrorMessage(translate(`${TRANSLATION_PATH}.unableToProcessPayment`));
        return;
      }

      await confirmPaymentIntent(await getAuthToken(), {
        payment_intent_id: session_id,
        plan_year: planYear,
      });

      if (handlePaymentSuccess) {
        await handlePaymentSuccess(customer_id);
      }
    } catch (err) {
      handleErrorMessage(err);
    } finally {
      setLoading(false);
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["listMedicarePrescriptionPaymentPlanDueInvoices"] }),
        queryClient.invalidateQueries({ queryKey: ["listMedicarePrescriptionPaymentPlanPaymentHistory"] }),
      ]);
    }
  };

  useEffect(() => {
    if (showPaymentMethodForm && elements) {
      const element = elements.getElement("payment");
      element.on("ready", () => {
        setLoading(false);
      });
    }
  });

  return (
    <form onSubmit={handleSubmit}>
      {showPaymentMethodForm && <PaymentElement onLoadError={handleLoadingError} />}
      <button ref={submitRef} type="submit" disabled={!stripe} style={{ display: "none" }}>
        {translate(`${TRANSLATION_PATH}.submitPaymentIntent`)}
      </button>
    </form>
  );
};

export const StripePaymentElement = ({
  amount,
  children,
  paymentMethod,
  onError,
}: PropsWithChildren & StripePaymentOptions & { readonly onError: () => void }) => {
  const { i18n } = useTranslation();

  const { memberEligibility } = useContext(MemberEligibilityContext);

  const stripePromise = useMemo(async () => {
    if (site?.stripe?.pubKey) {
      try {
        return loadStripe(site.stripe.pubKey, {
          stripeAccount: memberEligibility.plan.payment_vendor_account_id,
        });
      } catch {
        onError();
        return Promise.reject();
      }
    } else {
      return Promise.reject();
    }
  }, []);

  const options: StripeElementsOptionsMode = {
    locale: i18n?.language as StripeElementLocale || "auto",
    paymentMethodTypes: [paymentMethod === "ach" ? "us_bank_account" : paymentMethod],
    mode: "payment",
    currency: "usd",
    amount: new Decimal(amount).times(100).toNumber(),
    appearance: {
      theme: "flat",
      rules: {
        ".Input": {
          borderColor: "#AEBFCC",
          borderStyle: "solid",
          borderWidth: "1px",
          backgroundColor: "#ffffff",
          fontSize: "14px",
          padding: "10px",
        },
        ".Label": {
          fontSize: "12px",
          fontWeight: "600",
          marginBottom: "4px",
          color: "#313E48",
        },
      },
      variables: {
        colorPrimary: "#0570de",
        colorBackground: "#ffffff",
        fontFamily: "Source Sans Pro, sans-serif",
        spacingGridColumn: "12px",
        spacingGridRow: "12px",
        spacingUnit: "2px",
        borderRadius: "4px",
      },
    },
  };

  return (
    <>
      {site?.stripe?.pubKey && (
        <Elements options={options} stripe={stripePromise}>
          {children}
        </Elements>
      )}
    </>
  );
};
